import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Edit2, Shield, Star, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ArtefactTemplate } from '../../../types/artefact-templates'

interface TemplateCardProps {
  template: ArtefactTemplate
  isSystem?: boolean
  deleteConfirm?: boolean
  onEdit?: (() => void) | undefined
  onDelete?: (() => void) | undefined
  onDeleteConfirm?: (() => void) | undefined
  onDeleteCancel?: (() => void) | undefined
}

export function TemplateCard(props: TemplateCardProps): ReactElement {
  const { template, isSystem, deleteConfirm, onEdit, onDelete, onDeleteConfirm, onDeleteCancel } =
    props
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between">
        <CardSummary template={template} isSystem={isSystem} t={t} />
        {!isSystem && (
          <CardActions
            template={template}
            deleteConfirm={Boolean(deleteConfirm)}
            onEdit={onEdit}
            onDelete={onDelete}
            onDeleteConfirm={onDeleteConfirm}
            onDeleteCancel={onDeleteCancel}
            t={t}
          />
        )}
      </div>
    </div>
  )
}

interface CardSummaryProps {
  template: ArtefactTemplate
  isSystem?: boolean | undefined
  t: TFunction<'cases'>
}

function CardSummary({ template, isSystem, t }: CardSummaryProps): ReactElement {
  return (
    <div className="flex-1">
      <div className="items-centre flex gap-2">
        <h5 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">
          {template.name}
        </h5>
        {template.isDefault && (
          <span className="items-centre inline-flex gap-1 rounded-full bg-[#4e5e82]/10 px-2 py-0.5 text-xs font-medium text-[#5c6e94] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]">
            <Star className="h-3 w-3" />
            {t('collections.artefactTemplates.defaultBadge')}
          </span>
        )}
        {isSystem && (
          <span className="items-centre inline-flex gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <Shield className="h-3 w-3" />
            {t('collections.artefactTemplates.scopeSystem')}
          </span>
        )}
      </div>
      {template.description && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{template.description}</p>
      )}
      <div className="items-centre mt-2 flex gap-4 text-xs text-slate-400 dark:text-slate-500">
        <span>
          {t('collections.artefactTemplates.artefactCount', { count: template.artefactCount })}
        </span>
        {template.supportedPlatforms.length > 0 && (
          <span>{template.supportedPlatforms.join(', ')}</span>
        )}
      </div>
    </div>
  )
}

interface CardActionsProps {
  template: ArtefactTemplate
  deleteConfirm: boolean
  onEdit?: (() => void) | undefined
  onDelete?: (() => void) | undefined
  onDeleteConfirm?: (() => void) | undefined
  onDeleteCancel?: (() => void) | undefined
  t: TFunction<'cases'>
}

function CardActions(props: CardActionsProps): ReactElement {
  const { template, deleteConfirm, onEdit, onDelete, onDeleteConfirm, onDeleteCancel, t } = props
  return (
    <div className="items-centre flex gap-1">
      {deleteConfirm ? (
        <DeleteConfirmRow
          templateName={template.name}
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
          t={t}
        />
      ) : (
        <EditDeleteButtons onEdit={onEdit} onDelete={onDelete} t={t} />
      )}
    </div>
  )
}

interface DeleteConfirmRowProps {
  templateName: string
  onConfirm?: (() => void) | undefined
  onCancel?: (() => void) | undefined
  t: TFunction<'cases'>
}

function DeleteConfirmRow({
  templateName,
  onConfirm,
  onCancel,
  t,
}: DeleteConfirmRowProps): ReactElement {
  return (
    <div className="items-centre flex gap-2">
      <span className="text-xs text-red-600 dark:text-red-400">
        {t('collections.artefactTemplates.deleteConfirmMessage', { name: templateName })}
      </span>
      <button
        onClick={onConfirm}
        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
      >
        {t('collections.artefactTemplates.deleteTemplate')}
      </button>
      <button
        onClick={onCancel}
        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {t('collections.artefactTemplates.cancel')}
      </button>
    </div>
  )
}

interface EditDeleteButtonsProps {
  onEdit?: (() => void) | undefined
  onDelete?: (() => void) | undefined
  t: TFunction<'cases'>
}

function EditDeleteButtons({ onEdit, onDelete, t }: EditDeleteButtonsProps): ReactElement {
  return (
    <>
      <button
        onClick={onEdit}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('collections.artefactTemplates.editTemplate')}
      >
        <Edit2 className="h-4 w-4" />
      </button>
      <button
        onClick={onDelete}
        className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400"
        aria-label={t('collections.artefactTemplates.deleteTemplate')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </>
  )
}
